import { CloseOutlined } from '@ant-design/icons';
import { Col, Empty, Modal, Row } from 'antd';
import Title from 'antd/es/typography/Title';
import { companySetup } from 'Api/companySetup';
import { workersCompensationApi } from 'Api/workersCompensation';
import Buttons from 'components/Global/Buttons';
import CheckBox from 'components/Global/Checkbox';
import DatePickerField from 'components/Global/DatePicker';
import InputField from 'components/Global/InputField';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
	getFinancialYearRange,
	hasFormError,
	toastText,
	validateFormData,
} from 'utils/utils';
import { fetchConstantDropdownAction } from '../../../../../redux/actions/constantDropdownAction';
import styles from './index.module.scss';
import './index.scss';

type CalculateCompensationModalProps = {
	open: boolean;
	cancelCalculateModal: () => void;
	fetchCompensationReportDetails: () => void;
};
const CalculateCompensationModal = (props: CalculateCompensationModalProps) => {
	const { open, cancelCalculateModal, fetchCompensationReportDetails } = props;
	const [loading, setLoading] = useState(false);
	const [selectAll, setSelectAll] = useState(false);
	const [currencyFields, setCurrencyFields] = useState<any>([]);
	const [currencyError, setCurrencyError] = useState<Boolean>(false);

	const [financialMonth, setFinancialMonth] = useState<number | null>(null);
	const [financialMonthLoading, setFinancialMonthLoading] =
		useState<boolean>(false);

	const dispatch = useDispatch<AppDispatch>();
	const constantDropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data,
	);

	const currencyOptions = constantDropdownOptions?.currencyData;

	const [calculationDetails, setCalculationDetails] = useState<any>({
		year: dayjs(new Date()),
		month: new Date().getMonth() + 1,
	});

	const [formError, setFormError] = useState<any>({
		year: false,
		month: false,
		currencyFields: false,
	});

	const [hasError, setHasError] = useState(false);

	const [financialYearRange, setFinancialYearRange] = useState<string | null>(
		null,
	);

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: false,
			onclick: cancelCalculateModal,
			minWidth: '10rem',
		},
		{
			text: 'Calculate',
			isLoading: loading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			onclick: handleSubmit,
			minWidth: '10rem',
		},
	];

	const handleCancelModal = () => {
		setFormError({
			year: false,
			month: false,
			currencyFields: false,
		});
		setCalculationDetails({
			year: null,
			month: null,
		});
		setHasError(false);
		setCurrencyFields([]);
		setCurrencyError(false);
		setSelectAll(false);
		cancelCalculateModal();
	};
	const handleCheckboxChange = (id: number, checked: boolean) => {
		const updatedData = currencyFields.map((item: any) =>
			item.id === id
				? {
						...item,
						checked,
						conversionRate: checked
							? item.isBaseCurrency
								? 1
								: item.conversionRate
							: null,
						error: false,
					}
				: item,
		);

		// Use the UPDATED array to compute selection error
		const anyChecked = updatedData.some((i: any) => i.checked);
		setCurrencyError(!anyChecked);

		setCurrencyFields(updatedData);
	};

	const handleCurrencyChange = (id: number, value: number) => {
		const updatedData = currencyFields.map((item: any) =>
			item.id === id ? { ...item, conversionRate: value, error: false } : item,
		);
		setCurrencyFields(updatedData);
	};

	async function handleSubmit() {
		// Base form validation (year)
		const checkFormError = validateFormData(
			{ ...calculationDetails },
			{ ...formError },
		);
		setFormError(checkFormError);

		if (hasFormError(checkFormError)) return;

		// Validate currency rows (non-base must have positive rate if checked)
		const CheckCurrencyError = currencyFields.some((item: any) => item.checked);

		setCurrencyError(!CheckCurrencyError);

		if (currencyFields.length === 0 || !CheckCurrencyError) {
			return;
		}

		// Safe year extraction
		const selectedYear = dayjs(calculationDetails.year).isValid()
			? dayjs(calculationDetails.year).year()
			: dayjs().year();

		// Build payload
		const currencyArray: any[] = [];

		currencyFields.forEach((item: any) => {
			if (item.checked) {
				currencyArray.push({
					currencyId: item.id,
					currencyRate: 1, // Always pass 1 as conversion rate
					currencyType: item.label,
				});
			}
		});

		try {
			setLoading(true);
			const data = {
				year: selectedYear,
				currencies: currencyArray,
			};

			await workersCompensationApi.calculateCompensation(data);

			toastText('Compensation calculated Successfully', 'success');
			fetchCompensationReportDetails();
			handleCancelModal();
		} catch (error: any) {
			// Surface backend error to the user
			const errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				'Something went wrong calculating compensation report';
			toastText(errorMessage, 'error');
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (open) {
			setCalculationDetails({
				year: dayjs(new Date()),
			});
			setFormError({
				year: false,
				currencyFields: false,
			});
			setCurrencyError(false);
		}
	}, [open]);

	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	useEffect(() => {
		if (currencyOptions?.length > 0 && currencyFields.length === 0) {
			const baseCurrency = currencyOptions.find(
				(currency: any) => currency?.isBaseCurrency,
			);
			const remainingCurrencies = currencyOptions.filter(
				(currency: any) => !currency?.isBaseCurrency && currency?.value,
			);
			const allCurrencies = [baseCurrency, ...remainingCurrencies];
			const initialCurrencyFields = allCurrencies
				.filter((currency: any) => currency?.value)
				.map((currency: any) => ({
					id: currency.value,
					label: currency.label,
					checked: currency.isBaseCurrency,

					isBaseCurrency: currency.isBaseCurrency,
				}));

			setCurrencyFields(initialCurrencyFields);
		}
	}, [currencyOptions, currencyFields.length]);

	const handleYearChange = (value: any) => {
		setCalculationDetails((prev: any) => ({
			...prev,
			year: value,
		}));

		// Update the financial year range based on the selected year and fiscal month
		if (financialMonth !== null) {
			const fiscalYearRange = getFinancialYearRange(
				dayjs(value).year(),
				financialMonth,
			);
			setFinancialYearRange(fiscalYearRange); // Update state with new financial year range
		}
	};

	const fetchFinancialYear = async () => {
		try {
			setFinancialMonthLoading(true);
			const financialYear = await companySetup.getFinancialYear();
			const _month = (financialYear?.data?.data ?? 1) - 1; // backend returns 1-12
			setFinancialMonth(_month);

			// Calculate financial year range for the initial year
			const fiscalYearRange = getFinancialYearRange(dayjs().year(), _month);
			setFinancialYearRange(fiscalYearRange);
		} catch (error: any) {
			setFinancialYearRange(null);
		} finally {
			setFinancialMonthLoading(false);
		}
	};

	useEffect(() => {
		fetchFinancialYear();
	}, []);

	return (
		<>
			<Modal
				open={open}
				width={500}
				maskClosable={true}
				closable={false}
				onCancel={handleCancelModal}
				footer={null}
				style={{ position: 'absolute', top: 150, right: 50 }}
			>
				<div className={`${styles['modal']} modal`}>
					<div className={styles['modal-header']}>
						<Title level={4}>Compensation</Title>
						<div className={styles['close-icon']} onClick={handleCancelModal}>
							<CloseOutlined />
						</div>
					</div>
					<div>
						<Row className={styles['form-container-card']} gutter={50}>
							<Col span={24} className={styles['col']}>
								<DatePickerField
									name="year"
									value={calculationDetails.year}
									label="Year"
									required={true}
									helperText="Year is required"
									picker="year"
									onChange={handleYearChange}
									// onChange={(value) => {
									// 	handleChange(value, 'year', true);
									// }}
									isError={formError.year}
									disabled={false}
								/>
							</Col>
							<Col span={24}>
								<div className={styles['field-container']}>
									{/* <div
										className={
											styles['field-container-label']
										}
									>
										<label
											className={
												styles[
													'side-drawer-form__napsa--label'
												]
											}
										>
											{'Financial Year'}{' '}
										</label>
									</div> */}
									<InputField
										name="financialYear"
										value={financialYearRange || 'Loading...'}
										// value={
										// 	calculationDetails.year && financialMonth
										// 		? payPeriodData.FinancialYearRange
										// 		: 'Loading...'
										//	 }
										label="Financial Year"
										required={false}
										helperText="Pay Period is required"
										onChange={(value) => {}}
										isError={false}
										disabled={true}
									/>
								</div>
							</Col>

							<Col span={24} className={styles['col']}>
								<div className={styles['form-group']}>
									<label className={styles['form-label']}>
										Please select the currency
										<span className={styles['required']}>* </span>
									</label>
								</div>
								{currencyFields.length === 0 && (
									<>
										<Empty />
									</>
								)}

								<Row gutter={[16, 8]}>
									{currencyFields?.map((item: any) => {
										if (item.id === '') {
											return <></>;
										} else {
											return (
												<Col key={item.id} xs={12} sm={8} md={6}>
													<div className={styles['currency-group']}>
														<CheckBox
															disabled={item.disabled}
															checked={item.checked}
															label={item.label}
															onChange={(checked) =>
																handleCheckboxChange(item.id, checked)
															}
														/>
													</div>
												</Col>
											);
										}
									})}
								</Row>

								{/* Exchange rate note for non-base currencies */}
								{currencyFields.some(
									(item: any) => item.checked && !item.isBaseCurrency,
								) && (
									<div
										className={styles['exchange-rate-note']}
										style={{
											marginTop: '12px',
											padding: '8px 12px',
											backgroundColor: '#f6f8fa',
											border: '1px solid #e1e4e8',
											borderRadius: '4px',
											fontSize: '13px',
											color: '#586069',
										}}
									>
										<strong>Note:</strong> Exchange rates will be retrieved from
										the payroll data that has been processed for the selected
										currency.
									</div>
								)}
							</Col>
							{currencyError && (
								<Col span={24} className={styles['col']}>
									<p className={styles['error-text']}>
										Please select One Currency
									</p>
								</Col>
							)}
						</Row>

						<Row className={styles['modal-buttons']}>
							<Buttons buttons={buttons} />
						</Row>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default CalculateCompensationModal;
